/**
 * Moving an online map to this device (desktop app; docs/plans/2026-09-29-
 * attachments-move-with-their-map.md, stage 3). In this order, so nothing is
 * lost:
 *   1. its attachments are copied into the device store (same IDs, keys and
 *      names rewrapped for the map's device key): from the offline copies
 *      where they exist, else downloaded; files attached offline and not sent
 *      yet join them;
 *   2. the complete map (the server's records merged with every copy on this
 *      device) is merged into its file in the vault folder;
 *   3. checked: the file holds all of it, and every attachment opens with its
 *      original size;
 *   4. only then: members removed (each keeps what they need to recover a
 *      copy as it was), this device's channels to the map stopped, steps 1-3
 *      once more for changes that reached the server meanwhile, the online
 *      copy deleted, and the map listed on this device instead of online (an
 *      open map reopens from its file).
 * Until the deletion the online map stays as it was. The move is recorded on
 * the device: one that stopped earlier is offered again at the next start
 * (files already copied are not downloaded again) or cancelled; one that
 * stopped after its final check is finished then.
 */
// Only light modules here: the vault list and the map pill import this one
// (canMoveToDevice). What a move needs is loaded when a move runs.
import { VaultStorage } from '../crypto/vault-storage.js';
import { Vault } from '../crypto/vault.js';
import * as UserCRDT from '../sync/user-crdt-bridge.js';
import { uint8ToBase64, base64ToUint8 } from './attachment-envelope.js';
import { mergeIntoMapFile, readMapFile, deleteStoredMapAttachments, flushPendingWrites } from '../storage/index.js';
import { t } from '../../shared/i18n/index.js';

const headers = { 'X-Requested-With': 'XMLHttpRequest' };

// ---- The record of a move, kept on this device ----
// { [docId]: { title, account, startedAt, phase: 'copy' | 'finish', entry } }
// `account`: the account's public key (as for moves online), known from the
// vault as soon as it loads.
// 'finish': the device copy was complete and checked when the online copy
// started to be deleted. `entry`: the online entry's title, dates and
// colours, for the map's entry on this device.
const RECORDS_KEY = 'orbital-moves-to-device';

function readRecords() {
  try { return JSON.parse(localStorage.getItem(RECORDS_KEY) || '{}') || {}; } catch { return {}; }
}

function writeRecord(docId, record) {
  try { localStorage.setItem(RECORDS_KEY, JSON.stringify({ ...readRecords(), [docId]: record })); } catch { /* not resumable, still works */ }
}

function clearRecord(docId) {
  const records = readRecords();
  if (!(docId in records)) return;
  delete records[docId];
  try { localStorage.setItem(RECORDS_KEY, JSON.stringify(records)); } catch { /* left: offered again, then cleared */ }
}

const currentAccount = () => VaultStorage.getIndex?.()?.keypair?.publicKey || null;

// Maps being moved to this device right now, in this tab (moveToDevice).
const running = new Set();

/** Moves to this device that stopped before the end, for the signed-in account (not one running here). */
export function unfinishedMovesToDevice() {
  const account = currentAccount();
  if (!account) return [];
  return Object.entries(readRecords())
    .filter(([docId, record]) => record?.account === account && !running.has(docId))
    .map(([docId, record]) => ({ docId, title: record.title, phase: record.phase }));
}

export function isDesktopApp() {
  return typeof window !== 'undefined' && typeof window.__TAURI_INTERNALS__ !== 'undefined';
}

/**
 * Whether moves to this device can be decided here: the desktop app, with
 * both the device's list of maps and the account's loaded (at start the
 * account's may come first: VAULT_INDEX_UPDATED follows each).
 */
export function movesToDeviceDecidable() {
  return isDesktopApp() && !!UserCRDT.isLocalEnabled?.() && !!UserCRDT.isOnlineEnabled?.();
}

/** Whether this map can be moved to this device here: the desktop app, an online map this account owns. */
export function canMoveToDevice(docId) {
  if (!docId || !movesToDeviceDecidable()) return false;
  if (UserCRDT.getDocStorage?.(docId) !== 'online') return false;
  const entry = UserCRDT.getDocEntry(docId);
  return !!entry && (!entry.role || entry.role === 'owner');
}

/** The map's members other than its owner (user IDs), from the server. */
async function memberIds(docId) {
  const response = await fetch(`/api/shares/${docId}/permissions`, { credentials: 'include', headers });
  if (!response.ok) throw new Error(t('app.mapStorage.toDeviceError.serverStatus', { status: response.status }));
  return (await response.json()).filter((key) => key.role !== 'owner').map((key) => key.user_id);
}

/**
 * What moving this map involves, for its confirmation: its attachments
 * (on the server, and attached offline not sent yet), its members, and how
 * many other maps link to it. Needs the server.
 * @returns {Promise<{files: number, members: number, links: number}>}
 */
export async function describeMoveToDevice(docId) {
  const response = await fetch(`/api/docs/${docId}/blobs`, { credentials: 'include', headers });
  if (!response.ok) throw new Error(t('app.mapStorage.toDeviceError.serverStatus', { status: response.status }));
  const { waitingCount } = await import('./waiting-attachments.js');
  const files = ((await response.json()).blobs || []).length + waitingCount([docId]);
  const members = (await memberIds(docId)).length;
  const links = VaultStorage.getLinkers(docId).filter((id) => id !== docId).length;
  return { files, members, links };
}

/** The map's key on this device (kept once created: the user-CRDT key record is never removed). */
async function deviceKey(docId) {
  let key = UserCRDT.getLocalMapKey(docId);
  if (!key) {
    key = UserCRDT.setLocalMapKey(docId, uint8ToBase64(Vault.generateDEK()));
    // Saved before any file is wrapped for it.
    await flushPendingWrites();
  }
  return crypto.subtle.importKey('raw', base64ToUint8(key), { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}

/** Steps 1 to 3: the attachments and the complete map on the device, checked. */
async function copyAndCheck(docId, localKey, report) {
  const [{ copyAttachmentsToDevice, checkDeviceAttachments }, { readCompleteMapState }, { serverHasLocalCopy }] = await Promise.all([
    import('./attachment-transfer.js'), import('../crypto/data-export.js'), import('../crypto/backup-state.js')]);
  const dekBytes = await VaultStorage.getDocDEK(docId);
  try {
    const onlineKey = await crypto.subtle.importKey('raw', dekBytes, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
    const files = await copyAttachmentsToDevice(docId, { fromKey: onlineKey, toKey: localKey, report });
    report({ label: t('app.mapStorage.savingOnDevice') });
    const { state, warnings } = await readCompleteMapState(docId, dekBytes);
    if (warnings.length) throw new Error(t('app.mapStorage.toDeviceError.incomplete', { error: warnings.join(' ') }));
    if (state) await mergeIntoMapFile(docId, state);
    report({ label: t('app.mapStorage.checkingCopy') });
    // The file holds everything the complete map does.
    const file = await readMapFile(docId);
    if (state && !(file && serverHasLocalCopy([file], state))) {
      throw new Error(t('app.mapStorage.toDeviceError.contentMismatch'));
    }
    await checkDeviceAttachments(docId, localKey, files, report);
  } finally {
    dekBytes.fill(0);
  }
}

/** Its members lose access; the server keeps what each needs to recover a copy as it was. */
async function removeMembers(docId, report) {
  const members = await memberIds(docId);
  if (!members.length) return;
  report({ label: t('app.mapStorage.removingAccess') });
  const { rotateShareDEK } = await import('../sync/share-rotation.js');
  await rotateShareDEK(docId, { revokeUserIds: members });
}

/** This device's channels to the map (the open map's, a linked view's), stopped before the online copy goes. */
async function stopChannels(docId) {
  const bridge = await import('../sync/crdt-bridge.js');
  // Edits in flight are sent and confirmed first; later ones keep saving to the file.
  if (bridge.getDocManager()?.docId === docId) await bridge.getSyncManager()?.disconnect();
  await bridge.stopLinkedChannel(docId);
}

/** Whether the server still has the map: 403 once it was deleted (its owner's access went with it). */
async function onlineCopyExists(docId) {
  const response = await fetch(`/api/shares/${docId}/permissions`, { credentials: 'include', headers });
  if (response.ok) return true;
  if (response.status === 403 || response.status === 404) return false;
  throw new Error(t('app.mapStorage.toDeviceError.serverStatus', { status: response.status }));
}

async function deleteOnlineCopy(docId, report) {
  report({ label: t('app.mapStorage.removingOnline') });
  const remove = () => fetch(`/api/docs/${docId}`, { method: 'DELETE', credentials: 'include', headers });
  let response = await remove();
  if (response.status === 409) {
    // Someone joined meanwhile (an invitation accepted): removed too.
    await removeMembers(docId, report);
    response = await remove();
  }
  if (!response.ok && response.status !== 404) {
    throw new Error(t('app.mapStorage.toDeviceError.serverStatus', { status: response.status }));
  }
}

/**
 * Move an online map to this device, or continue a move that stopped.
 *
 * @param {string} docId
 * @param {{report?: (event: object) => void, reopen?: (docId: string) => Promise<void>}} options
 *   reopen: reopens the map if it is the open one (after the move: from its
 *   file; after a failure that stopped its channel: online again)
 * While it runs, its record is not an "unfinished" move (unfinishedMovesToDevice).
 * @returns {Promise<{ok: boolean, error?: string}>}
 */
export async function moveToDevice(docId, options = {}) {
  running.add(docId);
  try { return await moveToDeviceNow(docId, options); }
  finally { running.delete(docId); }
}

async function moveToDeviceNow(docId, { report = () => {}, reopen = async () => {} } = {}) {
  const account = currentAccount();
  const earlier = readRecords()[docId];
  const record = account && earlier?.account === account ? earlier : null;
  const stillOnline = UserCRDT.getDocStorage?.(docId) === 'online';
  if (!(stillOnline ? canMoveToDevice(docId) : record?.phase === 'finish')) {
    return { ok: false, error: t('app.mapStorage.toDeviceError.notMovable') };
  }
  const online = stillOnline ? UserCRDT.getDocEntry(docId) : null;
  const entry = online ? { title: online.title || '', createdAt: online.createdAt, meta: online.meta } : record.entry;
  let stopped = false;
  let deleted = false;
  try {
    const exists = stillOnline && await onlineCopyExists(docId);
    // Gone from the server before this move checked a copy: nothing to move from.
    if (stillOnline && !exists && record?.phase !== 'finish') throw new Error(t('app.mapStorage.toDeviceError.goneOnline'));
    if (exists) {
      writeRecord(docId, { title: entry.title, account, startedAt: record?.startedAt || Date.now(), phase: record?.phase || 'copy', entry });
      const localKey = await deviceKey(docId);
      if (record?.phase !== 'finish') await copyAndCheck(docId, localKey, report);
      await removeMembers(docId, report);
      stopped = true;
      await stopChannels(docId);
      // Changes that reached the server meanwhile (another device's, a
      // member's before removal), with the map's key now in force.
      await copyAndCheck(docId, localKey, report);
      writeRecord(docId, { ...readRecords()[docId], phase: 'finish' });
      await deleteOnlineCopy(docId, report);
      deleted = true;
    }
    await VaultStorage.listOnThisDevice(docId, entry);
  } catch (error) {
    // The online map stays as it was: its channel runs again.
    if (stopped && !deleted && UserCRDT.getDocStorage?.(docId) === 'online') await reopen(docId).catch(() => {});
    return { ok: false, error: error?.message || String(error) };
  }
  // Nothing on this device talks to the server about it any more.
  await reopen(docId).catch((error) => console.warn('[MoveToDevice] Reopening the map failed:', error));
  VaultStorage.clearDEKCache(docId);
  // Its offline copies and files that waited to be sent are in the device store now.
  await deleteStoredMapAttachments('offline', docId).catch(() => {});
  const { deleteWaitingFiles } = await import('./waiting-attachments.js');
  await deleteWaitingFiles([docId]).catch(() => {});
  clearRecord(docId);
  return { ok: true };
}

/**
 * Give up a move that stopped before its final check: the files it copied
 * to the device go; the online map was never changed.
 */
export async function cancelMoveToDevice(docId) {
  const record = readRecords()[docId];
  if (!record || record.phase === 'finish') return;
  if (UserCRDT.getDocStorage?.(docId) === 'online') {
    await deleteStoredMapAttachments('device', docId).catch(() => {});
  }
  clearRecord(docId);
}
